import React, { createContext, useReducer, useEffect, useContext } from 'react';
import axios from 'axios';
import { API_URL } from '../utils/config';
// Import Firebase auth functions
import {
  auth,
  onAuthStateChanged,
  signOut as firebaseSignOut,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult
} from '../firebase'; // Adjust path if needed
import { trackCompleteRegistration } from '../utils/marketingAnalytics';
import { setMetaMatchingUser } from '../utils/metaPixel';

// Helper to set Auth token in headers (keep this)
export const setAuthToken = (token) => {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    // Keep x-auth-token if your backend uses it, otherwise remove
    // axios.defaults.headers.common['x-auth-token'] = token;
  } else {
    delete axios.defaults.headers.common['Authorization'];
    // delete axios.defaults.headers.common['x-auth-token'];
  }
};

// Initial state
const initialState = {
  token: localStorage.getItem('token'), // Backend JWT token
  isAuthenticated: false, // Based on backend token/user
  loading: true, // Unified loading state
  user: null, // Backend user profile
  firebaseUser: null, // Firebase user object (optional to keep)
  error: null
};

// Create context
export const AuthContext = createContext(initialState);

// Reducer function (add case for firebase user if needed, or rely on existing cases)
const authReducer = (state, action) => {
  switch (action.type) {
    case 'USER_LOADED':
      return {
        ...state,
        isAuthenticated: true,
        loading: false,
        user: action.payload, // Backend user profile
        error: null,
      };
    case 'FIREBASE_USER_SET': // Optional: if you want to track firebase user separately
       return {
         ...state,
         firebaseUser: action.payload,
       };
    case 'REGISTER_SUCCESS': // Keep existing cases
    case 'LOGIN_SUCCESS': // This will be triggered by backend sync after Firebase login too
      return {
        ...state,
        token: action.payload.token, // Backend token
        isAuthenticated: true,
        loading: false, // Might be set later by USER_LOADED
        error: null
        // user: action.payload.user // USER_LOADED will set this
      };
    case 'PROFILE_UPDATE_SUCCESS':
      return {
        ...state,
        user: action.payload, // Updated backend user profile
        loading: false,
        error: null
      };
    case 'AUTH_ERROR':
    case 'REGISTER_FAIL':
    case 'LOGIN_FAIL':
    case 'LOGOUT':
      localStorage.removeItem('token');
      setAuthToken(null); // Clear auth header
      return {
        ...state,
        token: null,
        isAuthenticated: false,
        loading: false,
        user: null,
        firebaseUser: null, // Clear firebase user on logout
        error: action.payload
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null
      };
    case 'SET_LOADING':
       return {
         ...state,
         loading: action.payload,
       };
    default:
      return state;
  }
};

// Provider component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Central function to sync Firebase login with backend
  const syncWithBackendOnFirebaseLogin = async (firebaseUser) => {
    if (!firebaseUser) {
      // This case should be handled by the logout logic in onAuthStateChanged
      dispatch({ type: 'LOGOUT' });
      return;
    }
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const idToken = await firebaseUser.getIdToken();
      const res = await axios.post(`${API_URL}/users/auth/firebase`, { idToken });

      if (!res.data || !res.data.token) {
        throw new Error('No backend token received after Firebase login');
      }

      // Set backend token in localStorage and axios headers
      localStorage.setItem('token', res.data.token);
      setAuthToken(res.data.token);

      // Dispatch backend login success and user loaded
      dispatch({ type: 'LOGIN_SUCCESS', payload: { token: res.data.token } });
      dispatch({ type: 'USER_LOADED', payload: res.data.user }); // Assuming backend sends user profile

    } catch (err) {
      console.error("Backend sync failed after Firebase login:", err);
      // Logout from Firebase if backend sync fails? Optional.
      // await firebaseSignOut(auth);
      dispatch({
        type: 'AUTH_ERROR',
        payload: err.response?.data?.msg || err.message || 'Backend sync failed'
      });
      // Ensure logout state is fully cleared if sync fails
      dispatch({ type: 'LOGOUT' });
    } finally {
      // Set loading false regardless of outcome here,
      // as the initial auth check is complete.
       dispatch({ type: 'SET_LOADING', payload: false });
    }
  };


  // Effect to handle Firebase auth state changes and initial load
  useEffect(() => {
    dispatch({ type: 'SET_LOADING', payload: true }); // Start loading

    // Restore email/password sessions on page load: these logins only have the
    // backend JWT in localStorage, and onAuthStateChanged below never fires a
    // user for them, so without this call a refresh looks like a logout.
    if (localStorage.getItem('token')) {
      loadUser();
    }

    // Handle redirect result when user returns from Firebase/Google login
    getRedirectResult(auth).then((result) => {
      if (result && result.user) {
        console.log("Firebase redirect login successful:", result.user.email);
        // onAuthStateChanged will handle the rest
      }
    }).catch((error) => {
      if (error.code && error.code !== 'auth/redirect-cancelled-by-user') {
        console.error("Firebase redirect result error:", error);
        dispatch({ type: 'AUTH_ERROR', payload: error.message });
      }
    });

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      // Optional: Dispatch to store firebase user object if needed elsewhere
      // dispatch({ type: 'FIREBASE_USER_SET', payload: user });

      if (user) {
        // User is signed in to Firebase. Sync with backend.
        // Check if we already have a backend token for this user to avoid redundant calls
        // This check might be complex if the token could expire.
        // For simplicity, let's sync every time Firebase confirms a user initially.
        //console.log("Firebase user detected, syncing with backend...");
        await syncWithBackendOnFirebaseLogin(user);
      } else {
        // User is signed out from Firebase. Ensure backend state reflects this.
        //console.log("No Firebase user detected, ensuring logout state...");
        // Only dispatch logout if currently authenticated to avoid unnecessary state changes
        if (state.isAuthenticated) {
           dispatch({ type: 'LOGOUT' });
           setMetaMatchingUser(null);
        }
         dispatch({ type: 'SET_LOADING', payload: false }); // Stop loading as auth check is done
      }
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount


  // --- Existing Auth Functions (Register, Login, UpdateProfile) ---
  // Keep these as they are for email/password flows

  // Register user (Email/Password)
  const register = async (formData) => {
    // ... (keep existing implementation)
    const config = { headers: { 'Content-Type': 'application/json' } };
    try {
      const res = await axios.post(`${API_URL}/users/register`, formData, config);
      if (!res.data || !res.data.token) throw new Error('No token received');
      localStorage.setItem('token', res.data.token);
      setAuthToken(res.data.token);
      dispatch({ type: 'REGISTER_SUCCESS', payload: res.data });
      // Load user after registration
      await loadUser(); // Use loadUser here
      // Meta audience: registration completed (marketing-gated inside tracker)
      trackCompleteRegistration();
      return true;
    } catch (err) {
      dispatch({ type: 'REGISTER_FAIL', payload: err.response?.data?.msg || 'Registration failed' });
      return false;
    }
  };

  // Login user (Email/Password)
  const login = async (formData) => {
    // ... (keep existing implementation)
     const config = { headers: { 'Content-Type': 'application/json' } };
    try {
      const res = await axios.post(`${API_URL}/users/login`, formData, config);
      if (!res.data || !res.data.token) throw new Error('No token received');
      localStorage.setItem('token', res.data.token);
      setAuthToken(res.data.token);
      dispatch({ type: 'LOGIN_SUCCESS', payload: res.data });
      // Load user after login
      await loadUser(); // Use loadUser here
      return true;
    } catch (err) {
      dispatch({ type: 'LOGIN_FAIL', payload: err.response?.data?.msg || 'Login failed' });
      return false;
    }
  };

  // Load user data (Used after email/password login/register)
  const loadUser = async () => {
    // ... (keep existing implementation)
    if (!localStorage.token) {
      // dispatch({ type: 'AUTH_ERROR' }); // Handled by onAuthStateChanged now
      return null;
    }
    setAuthToken(localStorage.token);
    try {
      const res = await axios.get(`${API_URL}/users/profile`);
      dispatch({ type: 'USER_LOADED', payload: res.data });
      // Advanced Matching for logged-in users (hashed em + external_id only)
      setMetaMatchingUser(res.data);
      return res.data;
    } catch (err) {
      dispatch({ type: 'AUTH_ERROR', payload: err.response?.data?.msg || 'Load user failed' });
      return null;
    }
  };


  // Update user profile
  const updateProfile = async (formData) => {
    // ... (keep existing implementation)
    const config = { headers: { 'Content-Type': 'application/json' } };
    try {
      const res = await axios.put(`${API_URL}/users/profile`, formData, config);
      dispatch({ type: 'PROFILE_UPDATE_SUCCESS', payload: res.data });
      return true;
    } catch (err) {
      dispatch({ type: 'AUTH_ERROR', payload: err.response?.data?.msg || 'Profile update failed' });
      return false;
    }
  };

  // --- Updated Logout ---
  const logout = async () => {
    try {
      // Sign out from Firebase first
      await firebaseSignOut(auth);
      // Then dispatch backend logout (clears token, user, etc.)
      // The onAuthStateChanged listener will also fire and confirm logout state.
      dispatch({ type: 'LOGOUT' });
      setMetaMatchingUser(null);
      //console.log("User logged out successfully.");
    } catch (error) {
      console.error("Firebase sign out error", error);
      // Still attempt to clear local state even if Firebase logout fails
      dispatch({ type: 'LOGOUT', payload: 'Firebase sign out failed' });
      setMetaMatchingUser(null);
    }
  };

  // Clear errors
  const clearErrors = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  // --- Function for components to trigger Firebase Login ---
  // Uses signInWithRedirect (full page redirect to Google, then back)
  // This avoids cross-origin popup issues with Safari ITP and Edge Tracking Prevention
  const loginWithFirebase = async (provider) => {
     dispatch({ type: 'SET_LOADING', payload: true });
     try {
       await signInWithRedirect(auth, provider);
       // Page will redirect to Google — user returns after authentication
       return true;
     } catch (error) {
       console.error("Firebase signInWithRedirect Error:", error);
       dispatch({
         type: 'AUTH_ERROR',
         payload: error.message || 'Firebase sign-in failed'
       });
       dispatch({ type: 'SET_LOADING', payload: false });
       return false;
     }
  };


  // Return the provider
  return (
    <AuthContext.Provider
      value={{
        // Add authState back for compatibility
        authState: state,
        // State properties
        token: state.token,
        isAuthenticated: state.isAuthenticated,
        loading: state.loading,
        user: state.user, // Your backend user profile
        firebaseUser: state.firebaseUser, // Optional: Firebase user object
        error: state.error,
        // Auth functions
        register, // Email/Password register
        login,    // Email/Password login
        loginWithFirebase, // Social login trigger
        logout,
        updateProfile,
        clearErrors,
        loadUser // Keep loadUser if needed elsewhere, but initial load uses onAuthStateChanged
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  return useContext(AuthContext);
};